<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width=800 :fullscreen="$vuetify.breakpoint.xsOnly" persistent fluid scrollable content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-spacer
      .text-xl.font-bold.ml-10 {{ $t('create_new_task') }}
      v-spacer
      v-btn(icon @click="showDialog = false")
        v-icon mdi-close
    v-divider

    v-card-text.pa-8.primary--text
      v-form(ref="form")
        .text-lg.font-medium.mb-4 {{ $t('fill_all_field_below') }}
        v-row(no-gutters)
          v-col(cols="12" md="8")
            yc-text-field(label="Task name" v-model="name" :rules="rules.not_empty" validate-on-blur)
        yc-autocomplete.mt-4(label="Choose your segment" :items="segments" v-model="selectedSegments" itemText="title" itemValue="id" :rules="rules.not_empty_array" :loading="isFetchingSegments")
        .mt-1.text-xs.font-normal.primary400--text {{ $t('create_task_desc_1') }}
        yc-autocomplete.mt-4(label="Choose your assigness" :items="members" v-model="selectedMembers" itemText="title" itemValue="id" :rules="rules.not_empty_array" :loading="isFetchingMembers")
        .mt-1.text-xs.font-normal.primary400--text {{ $t('create_task_desc_2') }}
        v-row.mb-8(no-gutters)
          v-col(cols="12" md="8")
            y-dialog(v-model="showCalendar" max-width="320px")
              template(v-slot:activator="{ on }")
                yc-text-field.mt-4(v-model="selectedDate" label="Select due date" background-color="basic200" readonly :on="on" :calendar="true")
              yc-date-picker(:parentDate.sync="dueDate" @change="onChangeDate" no-title)
        .text-lg.font-medium.mb-2 {{ $t('create_task_desc_3') }}
        .text-md {{ $t('create_task_desc_4') }}
        
        v-radio-group(v-model="setTemplate")
          v-radio(value="no")
            template(v-slot:label)
              .text-md.font-normal.primary--text {{ $t('create_task_template_no') }}
          v-radio(value='yes')
            template(v-slot:label)
              .text-md.font-normal.primary--text {{ $t('create_task_template_yes') }}
        div(v-if="setTemplate === 'yes'")
          connect-message-template(:message.sync="finalMessage")
    v-divider

    v-card-actions.py-4.px-6
      v-spacer
      v-btn.button-md.primary400--text(@click="showDialog = false" text) {{ $t('back') }}
      v-btn.button-md.white--text(@click="clickCreate" :loading="isLoading" color="info500" depressed) {{ $t('create_task') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ConnectMessageTemplate from '@/components/connect/ConnectMessageTemplate'

export default {
  components: { ConnectMessageTemplate },
  props: ['open', 'added', 'segment'],
  data: () => ({
    isLoading: false,
    name: '',
    segments: [],
    selectedSegments: null,
    members: [],
    selectedMembers: null,
    setTemplate: 'no',
    finalMessage: '',
    selectedDate: null,
    dueDate: null,
    rules: {
      not_empty: [v => v.trim().length || 'This field is required'],
      not_empty_array: [v => !!(v && v.length) || 'This field is required']
    },
    showCalendar: false,
    isFetchingSegments: false,
    isFetchingMembers: false,
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    }
  },
  mounted () {
    this.fetchSegments()
    this.fetchMembers()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchSegments () {
      this.isFetchingSegments = true
      this.$axios.get(`/connect/${this.company.slug}/groups?page_size=-1&exclude=customer`)
        .then(resp => {
          this.segments = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingSegments = false
        })
    },
    fetchMembers () {
      this.isFetchingMembers = true
      this.$axios.get(`/members/?company=${this.company.id}&app=connect&page_size=-1`)
        .then(resp => {
          const results = resp.data.results
          this.members = results.map(item => {
            const member = item
            member.title = `${item.user.first_name} ${item.user.last_name}`
            return member
          })
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingMembers = false
        })
    },
    clickCreate () {
      if (!this.$refs.form.validate()) {
        return
      }
      if (this.setTemplate === 'yes' && this.finalMessage.length) {
        this.createTemplate()
      } else {
        this.createTask()
      }
    },
    onChangeDate () {
      this.showCalendar = false
      this.selectedDate = this.dueDate
    },
    createTemplate () {
      this.isLoading = true
      const params = {
        template: this.finalMessage
      }
      this.$axios.post(`/connect/${this.company.slug}/tasks/templates`, params)
        .then(resp => {
          const templateId = resp.data.id
          this.createTask(templateId)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_create_task')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    createTask (templateId) {
      this.isLoading = true
      const params = {
        title: this.name,
      }
      if (this.selectedSegments?.length) {
        params.group = this.selectedSegments
      }
      if (templateId) {
        params.template = [templateId]
      }
      if (this.selectedMembers?.length) {
        params.assign_to = this.selectedMembers
      }
      if (this.selectedDate) {
        params.due_date = this.selectedDate
      }
      this.$axios.post(`/connect/${this.company.slug}/tasks`, params)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.task_created_successfully')])
          this.$emit('added')
          this.showDialog = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_create_task')])
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<i18n>
  {
    "en": {
      "create_task_desc_1": "You can choose up to 4 segments",
      "create_task_desc_2": "You can choose more than one",
      "create_task_desc_3": "Do you want to set message template?",
      "create_task_desc_4": "This message template will be pasted to your team's Whatsapp chat box automatically when they following up the task.",
      "create_task_template_no": "No, I don't want to set message template",
      "create_task_template_yes": "Yes, I want to set message template"
    },
    "id": {
      "create_task_desc_1": "Anda dapat memilih hingga 4 segmen",
      "create_task_desc_2": "Anda dapat memilih lebih dari satu",
      "create_task_desc_3": "Apakah Anda ingin menyetel template pesan?",
      "create_task_desc_4": "Template pesan ini akan ditempelkan ke kotak obrolan Whatsapp tim Anda secara otomatis saat mereka menindaklanjuti tugas.",
      "create_task_template_no": "Tidak, saya tidak ingin menyetel template pesan",
      "create_task_template_yes": "Ya, saya ingin mengatur template pesan"
    }
  }
  </i18n>